import { IRichTextCard } from './types';
import { RichText } from 'prismic-reactjs';
import CustomPrismicLink from '@components/CustomPrismicLink';
import withSlice from '@screens/SlicesContainer/withSlice';
import { mapRichTextCard } from './mapRichTextCard';
import { useContext, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { LayoutContext } from '@context/LayoutContext';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';

const RichTextCard: React.FC<IRichTextCard> = ({ text, anchorTargetId }) => {
  const { mainHeaderHeight } = useContext(LayoutContext);
  const containerRef = useRef<HTMLTableSectionElement>(null);
  const router = useRouter();

  useEffect(() => {
    const urlAnchorId = router.asPath.split('#')[1];

    if (
      urlAnchorId &&
      urlAnchorId === anchorTargetId &&
      containerRef.current?.id === urlAnchorId
    ) {
      const top =
        containerRef.current.getBoundingClientRect().top +
        window.scrollY -
        (mainHeaderHeight || 0);

      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, [router.asPath]);

  return (
    <>
      <section
        className="rich-text-card root"
        id={anchorTargetId}
        ref={containerRef}
      >
        <RichText
          render={text}
          serializeHyperlink={CustomPrismicLink}
          htmlSerializer={(type, element) => {
            if (type === 'image') {
              return (
                <div className="tw-flex tw-justify-center !tw-p-0">
                  <ResponsiveImage
                    className="tw-mx-a tw-flex tw-place-self-center"
                    src={element.url}
                    width={element.dimensions.width}
                    height={element.dimensions.height}
                  />
                </div>
              );
            }

            return null;
          }}
        />
      </section>
      <style jsx>{`
        .root > :global(div) {
          padding: var(--space-5);
        }

        .root :global(h2) {
          font-size: 32px;
          font-weight: var(--font-weight-light);
          line-height: 44px;
          margin-bottom: var(--space-4);
        }

        .root :global(h3 > strong) {
          display: block;
          font-size: var(--text-size-h2);
          font-weight: var(--font-weight-bold);
          line-height: 32.4px;
          text-align: center;
          width: auto;
        }

        .root :global(p):not(:last-child) {
          margin-bottom: var(--space-3);
        }

        .root :global(a) {
          color: var(--clr-link);
          text-decoration: underline;
        }

        .root :global(a:hover) {
          opacity: var(--opacity-link-hover);
        }
      `}</style>
    </>
  );
};

/**
 * @typedef {Object} IRichTextCard
 * @property {string} text - Description of the text prop.
 * @property {string} anchorTargetId - Anchor target id.
 */

/**
 * @type {React.FC<IRichTextCard>}
 */

export default withSlice(RichTextCard, mapRichTextCard);
